import { useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import { MonoMeta } from "@/design/primitives";
import { useMediaQuery } from "@/hooks/useMediaQuery";

// The phone board at /phone — the call loop's surface, structured like the
// email response loop (Jeff 2026-10-22: "I'd like to have that set up using
// voice messages or just be about phone calls ... calls coming in, where
// they came from, the transcript of it, and whether you handled it or not").
//
// Same three-pane Airmail structure Jeff approved for /email/loop:
//   cool-gray sidebar (the lines) · dense date-grouped stream · reading pane
//
// The record's contract underneath: every Jeff-facing label comes from the
// payload's OWN wording table, the payload stays redacted (last-4 numbers,
// no caller names, no transcripts — those live on Kit's machine and mount
// here only through a future machine-local surface), and each row answers
// the three questions in one glance: who called, on which line, handled?

const FALLBACK_WORDING = {
  tab: "Phone",
  sections: { stream: "The calls", lines: "The lines", handled: "Handled or not" },
  handled: {
    handled: "Answered — Sona or a person picked up",
    missed: "Missed — no one picked up",
    unattended: "Reached nobody — check what happened",
    outgoing: "Kit called out",
  },
  direction: { incoming: "Incoming", outgoing: "Outgoing" },
  transcript: "Transcript on this machine",
  no_transcript: "No transcript",
};

const HANDLED_TONE = {
  handled: "text-os-green",
  missed: "text-os-red",
  unattended: "text-os-amber",
  outgoing: "text-os-ink-2",
};

const _handledTone = (key) => HANDLED_TONE[key] || "text-os-ink-2";

const LineRow = ({ line, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`phone-line-${line.brand || "unknown"}`}
    className={cn(
      "w-full border px-3 py-2.5 text-left transition-colors",
      selected
        ? "border-os-border bg-os-paper"
        : "border-transparent hover:bg-os-paper/60",
    )}
  >
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-[0.8125rem] font-medium text-os-ink">
        {line.line}
      </span>
      <span className="text-[0.6875rem] text-os-ink-2">{line.calls}</span>
    </div>
    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-os-ink-2">
      <span>{line.handled} handled</span>
      <span aria-hidden>·</span>
      <span>{(line.missed || 0) + (line.unattended || 0)} not</span>
    </div>
  </button>
);

const CallRow = ({ row, selected, onSelect }) => (
  <li data-testid={`phone-row-${row.call_token}`}>
    <button
      type="button"
      onClick={() => onSelect(row)}
      className={cn(
        "w-full border-l-2 px-4 py-3 text-left transition-colors",
        selected
          ? "border-os-bronze bg-os-paper"
          : "border-transparent hover:bg-os-paper/50",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <span className="truncate text-[0.875rem] font-medium text-os-ink">
          {row.other_display}
          <span className="ml-2 text-[0.6875rem] font-normal text-os-ink-2">
            {row.direction_label}
            {row.duration > 0 ? ` · ${row.duration}s` : ""}
          </span>
        </span>
        <span className="shrink-0 text-[10px] text-os-ink-2">{row.when}</span>
      </div>
      <div className="mt-0.5 flex items-center gap-2 text-[10px]">
        <span className={cn("font-medium", _handledTone(row.handled))}>
          {row.handled_label}
        </span>
        <span aria-hidden>·</span>
        <span className="text-os-ink-2">{row.line}</span>
        {row.has_transcript ? (
          <span
            data-testid={`phone-transcript-chip-${row.call_token}`}
            className="rounded-chip border border-os-border px-2 py-0.5 text-os-ink-2"
          >
            transcript saved
          </span>
        ) : null}
      </div>
    </button>
  </li>
);

const Reader = ({ row, wording }) => {
  if (!row) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center">
        <p className="max-w-[26rem] text-[0.875rem] leading-[1.5] text-os-ink-2">
          Pick a call from the stream — who it was, which line it hit, and
          whether it was handled.
        </p>
      </div>
    );
  }
  return (
    <div className="px-6 py-5" data-testid="phone-reader">
      <MonoMeta data-testid="phone-reader-meta">
        {row.line} · {row.direction_label} · {row.when}
      </MonoMeta>
      <h2
        className="mt-1 text-[1.375rem] font-semibold tracking-[-0.01em] text-os-ink"
        data-testid="phone-reader-title"
      >
        {row.other_display}
      </h2>
      <p
        className={cn("mt-2 text-[0.8125rem] font-medium", _handledTone(row.handled))}
        data-testid="phone-reader-handled"
      >
        {row.handled_label}
      </p>
      <div className="mt-4 border-t border-os-border pt-4 text-[0.8125rem] leading-[1.6] text-os-ink-2">
        <p data-testid="phone-reader-transcript-note">
          {row.has_transcript
            ? "A transcript of this call is saved on Kit's machine — it never leaves it. This pane shows the shape now; the full read lands with the machine-local surface."
            : "No transcript exists for this call."}
        </p>
        <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1.5 text-[11px]">
          <dt className="text-os-ink-2">Status</dt>
          <dd className="text-os-ink">{row.status}</dd>
          <dt className="text-os-ink-2">Duration</dt>
          <dd className="text-os-ink">{row.duration > 0 ? `${row.duration}s` : "0s"}</dd>
          <dt className="text-os-ink-2">Number</dt>
          <dd className="text-os-ink">{row.other_display}</dd>
        </dl>
      </div>
    </div>
  );
};

const dayKey = (row) => (row.when_iso || "").slice(0, 10);

export function PhoneBoard({ payload }) {
  const desktop = useMediaQuery("(min-width: 1024px)");
  const wording = { ...FALLBACK_WORDING, ...(payload?.wording || {}) };
  const stream = useMemo(
    () => payload?.stream || [], [payload]);
  const lines = useMemo(
    () => payload?.lines || [], [payload]);
  const tiles = payload?.tiles || {};

  const [selectedLine, setSelectedLine] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);

  const visible = useMemo(
    () => (selectedLine ? stream.filter((r) => r.line === selectedLine) : stream),
    [stream, selectedLine],
  );
  const selected = visible.find((r) => r.call_token === selectedToken) || null;

  // date groups, newest first — the email stream's rhythm
  const groups = useMemo(() => {
    const map = new Map();
    for (const r of visible) {
      const k = dayKey(r);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return [...map.entries()];
  }, [visible]);

  return (
    <div
      data-testid="phone-board"
      className={cn(
        "flex w-full overflow-hidden border border-os-border bg-os-bg",
        desktop ? "h-[calc(100vh-13rem)] min-h-[30rem]" : "min-h-[60vh] flex-col",
      )}
    >
      {/* ── sidebar: the lines ─────────────────────────────────────────── */}
      <aside
        className={cn(
          "flex flex-col border-os-border bg-os-shell",
          desktop ? "w-64 shrink-0 border-r" : "border-b",
        )}
        data-testid="phone-lines"
      >
        <div className="px-4 pb-2 pt-4">
          <h2 className="text-[0.6875rem] font-semibold uppercase tracking-[0.08em] text-os-ink-2">
            {wording.sections.lines}
          </h2>
        </div>
        <div className="flex-1 overflow-y-auto px-2 pb-3">
          <LineRow
            line={{ line: "All lines", brand: "", calls: stream.length,
                    handled: tiles.handled || 0,
                    missed: (tiles.missed || 0) + (tiles.unattended || 0),
                    outgoing: tiles.outgoing || 0 }}
            selected={selectedLine === null}
            onClick={() => { setSelectedLine(null); setSelectedToken(null); }}
          />
          {lines.map((l) => (
            <LineRow
              key={l.line}
              line={l}
              selected={selectedLine === l.line}
              onClick={() => { setSelectedLine(l.line); setSelectedToken(null); }}
            />
          ))}
        </div>
      </aside>

      {/* ── stream: the calls ──────────────────────────────────────────── */}
      <section
        className={cn(
          "flex min-w-0 flex-1 flex-col border-os-border",
          desktop ? "border-r" : "",
        )}
        data-testid="phone-stream"
      >
        <header className="flex items-baseline justify-between border-b border-os-border px-5 py-3">
          <h2 className="font-mono text-[0.6875rem] uppercase tracking-[0.08em] text-os-ink-2">
            {wording.sections.stream}
          </h2>
          <MonoMeta data-testid="phone-stream-count">
            {visible.length}
            {stream.length !== visible.length ? ` of ${stream.length}` : ""}
          </MonoMeta>
        </header>
        <div className="flex-1 overflow-y-auto">
          {groups.map(([day, rows]) => (
            <div key={day}>
              <div className="bg-os-shell px-5 py-1.5 font-mono text-[10px] uppercase tracking-[0.08em] text-os-ink-2">
                {rows[0].when.split(", ").slice(0, 2).join(", ")}
              </div>
              <ul>
                {rows.map((r) => (
                  <CallRow
                    key={r.call_token}
                    row={r}
                    selected={r.call_token === selectedToken}
                    onSelect={(row) => setSelectedToken(row.call_token)}
                  />
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ── reader ─────────────────────────────────────────────────────── */}
      <section
        className={cn(
          "min-w-0 flex-1 bg-os-bg",
          desktop ? "" : "border-t border-os-border",
        )}
        data-testid="phone-reader-pane"
      >
        <Reader row={selected} wording={wording} />
      </section>
    </div>
  );
}

export default PhoneBoard;